import { type ReactNode, useMemo } from 'react';
import { Circle } from 'lucide-react';

import type {
  PayerInformationT,
  PriorAuthSummaryT,
  PriorAuthorizationAnswerDetailT,
  PriorAuthorizationStatusLogEntryT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { usePatientEligibilitiesQuery } from '@/entities/eligibility';
import { PriorAuthAttachmentsList, usePriorAuthDetailsQuery } from '@/entities/prior-auth';
import { usePharmacyByIdQuery, usePrescriptionQuery } from '@/entities/rx';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/shared/ui/shadcn/sheet';
import { statusToneClassName } from '@/shared/ui/statusTone';

import {
  describePriorAuth,
  describePriorAuthHistoryStatus,
  formatMedicationName,
} from '../model/priorAuthPresentation';

interface PriorAuthCaseDrawerProps {
  userId: string;
  summary: PriorAuthSummaryT | null;
  onOpenChange: (open: boolean) => void;
  onOpenRequest: (summary: PriorAuthSummaryT) => void;
}

function formatDate(value: string | null | undefined, includeTime = false): string {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(includeTime ? { hour: 'numeric', minute: '2-digit' } : {}),
  });
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return String(value);
}

function displayQuantity(value: unknown): string {
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? String(number) : displayValue(value);
}

interface DetailRow {
  label: string;
  value: ReactNode;
}

function DetailList({ items }: { items: readonly DetailRow[] }) {
  return (
    <dl>
      {items.map((item) => (
        <div
          key={item.label}
          className="grid grid-cols-[128px_minmax(0,1fr)] gap-4 py-2.5 first:pt-0 last:pb-0"
        >
          <dt className="text-sm font-medium text-muted-foreground">{item.label}</dt>
          <dd className="min-w-0 text-sm leading-relaxed text-foreground">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

const SECTION_HEADING_CLASS = 'text-base font-medium tracking-tight text-foreground';

function formatAnswer(detail: PriorAuthorizationAnswerDetailT): string {
  const values = [
    detail.Answer,
    detail.AnswerFreeText,
    detail.AnswerNumeric,
    detail.AnswerDateTime,
  ].filter((value) => value !== null && value !== undefined && value !== '');
  return values.length > 0 ? values.map(String).join(' · ') : 'No answer recorded';
}

function resolveEligibility(
  items: readonly PayerInformationT[],
  eligibilityId: unknown
): PayerInformationT | undefined {
  if (eligibilityId === null || eligibilityId === undefined) {
    return undefined;
  }
  return items.find((item) => String(item.PatientEligibilityId) === String(eligibilityId));
}

export function PriorAuthCaseDrawer({
  userId,
  summary,
  onOpenChange,
  onOpenRequest,
}: PriorAuthCaseDrawerProps) {
  const detailsQuery = usePriorAuthDetailsQuery(summary?.id);
  const prescriptionQuery = usePrescriptionQuery(userId, summary?.dosespotPrescriptionId ?? '');
  const eligibilitiesQuery = usePatientEligibilitiesQuery(userId);
  const pharmacyId = prescriptionQuery.data?.PharmacyId;
  const pharmacyQuery = usePharmacyByIdQuery(
    pharmacyId === null || pharmacyId === undefined ? undefined : String(pharmacyId)
  );

  const details = detailsQuery.data;
  const dosespotCase = details?.dosespotCase;
  const caseDetails = dosespotCase?.case;
  const prescription = prescriptionQuery.data;
  const presentation = summary ? describePriorAuth(summary) : null;
  const eligibility = resolveEligibility(
    eligibilitiesQuery.data?.items ?? [],
    prescription?.EligibilityId
  );
  const insuranceLabel = eligibility?.PayerName ?? eligibility?.PlanName;
  const medicationLabel = prescriptionQuery.isError
    ? 'Prior authorization'
    : formatMedicationName(prescription?.DisplayName, prescription?.Strength);
  const diagnosis = prescription?.FirstPrescriptionDiagnosis?.PrimaryDiagnosis;
  const diagnosisLabel = [diagnosis?.DiagnosisCode, diagnosis?.DiagnosisDescription]
    .filter(Boolean)
    .join(' · ');
  const pharmacyLabel = pharmacyQuery.data?.StoreName;
  const finalStatus = caseDetails?.FinalStatus;
  const attachments = caseDetails?.Attachments ?? [];

  const history = useMemo(() => {
    const entries = dosespotCase?.statusLog?.StatusLog ?? dosespotCase?.history?.StatusLogs ?? [];
    return [...entries].sort((left, right) => {
      const leftTime = left.CreatedDateStamp ? new Date(left.CreatedDateStamp).getTime() : 0;
      const rightTime = right.CreatedDateStamp ? new Date(right.CreatedDateStamp).getTime() : 0;
      return rightTime - leftTime;
    });
  }, [dosespotCase]);

  const answeredQuestions = useMemo(() => {
    if (dosespotCase?.answeredQuestions?.length) {
      return dosespotCase.answeredQuestions.map((answered) => ({
        question: answered.questionText ?? answered.question?.QuestionText ?? 'Question',
        answers: (answered.answers ?? []).map(formatAnswer),
      }));
    }
    return (dosespotCase?.history?.QuestionSets ?? []).flatMap((set) =>
      (set.AnsweredQuestions ?? []).map((answered) => ({
        question: answered.Question ?? 'Question',
        answers: (answered.Answers ?? []).map(formatAnswer),
      }))
    );
  }, [dosespotCase]);

  const pendingQuestion = dosespotCase?.pendingQuestion?.QuestionText;
  const expectedResponse = caseDetails?.NextResponseDeadline
    ? formatDate(caseDetails.NextResponseDeadline)
    : null;
  const waitingForInsurance =
    summary?.status === 'Requested' ||
    summary?.status === 'Appealing' ||
    summary?.status === 'Cancel Requested';

  return (
    <Sheet open={summary !== null} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col bg-background p-0 sm:max-w-xl">
        <SheetHeader className="border-b border-border/70 px-7 py-6 text-left">
          <SheetTitle className="pr-8 text-lg">{medicationLabel}</SheetTitle>
          <SheetDescription>
            {[
              prescription?.Directions,
              prescription?.DaysSupply ? `${prescription.DaysSupply}-day supply` : null,
            ]
              .filter(Boolean)
              .join(' · ') || 'Prior authorization'}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-7 overflow-y-auto px-7 py-7">
          {detailsQuery.isError ? (
            <Alert variant="destructive">
              <AlertTitle>Could not load this prior authorization</AlertTitle>
              <AlertDescription>Try closing this panel and opening it again.</AlertDescription>
            </Alert>
          ) : detailsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading prior authorization…</p>
          ) : (
            <>
              {prescriptionQuery.isError ? (
                <Alert variant="destructive">
                  <AlertTitle>Could not load prescription details</AlertTitle>
                  <AlertDescription>
                    <p>The prior authorization is still available below.</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => void prescriptionQuery.refetch()}
                      disabled={prescriptionQuery.isFetching}
                    >
                      {prescriptionQuery.isFetching ? 'Trying again…' : 'Try again'}
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : null}

              {presentation ? (
                <section className="rounded-[12px] bg-surface-sunken p-5">
                  <Badge className={statusToneClassName(presentation.tone)}>
                    {presentation.label}
                  </Badge>
                  <h3 className="mt-3 text-base font-medium leading-relaxed tracking-tight">
                    {presentation.description}
                  </h3>
                  {waitingForInsurance ? (
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      No action is needed from you
                      {expectedResponse
                        ? `. We expect an update by ${expectedResponse}.`
                        : ' right now.'}
                    </p>
                  ) : null}
                  {summary && details?.allowedActions.length ? (
                    <Button
                      className="mt-4 rounded-full"
                      onClick={() => {
                        onOpenChange(false);
                        onOpenRequest(summary);
                      }}
                    >
                      {presentation.actionLabel}
                    </Button>
                  ) : null}
                </section>
              ) : null}

              <section className="space-y-4" hidden={prescriptionQuery.isError}>
                <h3 className={SECTION_HEADING_CLASS}>Prescription</h3>
                <DetailList
                  items={[
                    { label: 'Instructions', value: displayValue(prescription?.Directions) },
                    { label: 'Quantity', value: displayQuantity(prescription?.Quantity) },
                    { label: 'Days supply', value: displayValue(prescription?.DaysSupply) },
                    { label: 'Diagnosis', value: displayValue(diagnosisLabel) },
                    { label: 'Insurance', value: displayValue(insuranceLabel) },
                    { label: 'Pharmacy', value: displayValue(pharmacyLabel) },
                    { label: 'Prescribed', value: formatDate(prescription?.WrittenDate) },
                  ]}
                />
              </section>

              {finalStatus ? (
                <section className="space-y-4 rounded-[12px] bg-surface-sunken p-5">
                  <h3 className={SECTION_HEADING_CLASS}>Insurance decision</h3>
                  <DetailList
                    items={[
                      {
                        label: 'Authorization',
                        value: displayValue(finalStatus.AuthorizationNumber),
                      },
                      {
                        label: 'Effective',
                        value: formatDate(finalStatus.AuthorizationPeriodStart),
                      },
                      {
                        label: 'Expires',
                        value: formatDate(finalStatus.AuthorizationPeriodEnd),
                      },
                      {
                        label: 'Approved quantity',
                        value: displayValue(finalStatus.Quantity),
                      },
                      {
                        label: 'Approved days',
                        value: displayValue(finalStatus.DaysSupply),
                      },
                      { label: 'Decision note', value: displayValue(finalStatus.Note) },
                    ]}
                  />
                </section>
              ) : null}

              {pendingQuestion || answeredQuestions.length > 0 ? (
                <section className="space-y-4">
                  <h3 className={SECTION_HEADING_CLASS}>Questions from insurance</h3>
                  {pendingQuestion ? (
                    <div className="rounded-[12px] border border-amber-500/30 bg-amber-500/[0.06] p-4">
                      <p className="text-sm leading-relaxed">{pendingQuestion}</p>
                      <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                        Your answer is needed
                      </p>
                    </div>
                  ) : null}
                  {answeredQuestions.map((question, index) => (
                    <div
                      key={`${question.question}-${index}`}
                      className="rounded-[12px] bg-surface-sunken p-4"
                    >
                      <p className="text-sm leading-relaxed">{question.question}</p>
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                        {question.answers.join(' · ') || 'No answer recorded'}
                      </p>
                    </div>
                  ))}
                </section>
              ) : null}

              <PriorAuthHistory entries={history} fallbackStatus={caseDetails?.CurrentStatus} />

              <PriorAuthAttachmentsList recordId={summary?.id ?? ''} attachments={attachments} />
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function PriorAuthHistory({
  entries,
  fallbackStatus,
}: {
  entries: readonly PriorAuthorizationStatusLogEntryT[];
  fallbackStatus: string | null | undefined;
}) {
  const visibleEntries =
    entries.length > 0
      ? entries
      : fallbackStatus
        ? [{ Status: fallbackStatus } satisfies PriorAuthorizationStatusLogEntryT]
        : [];
  if (visibleEntries.length === 0) {
    return null;
  }

  return (
    <section>
      <h3 className={`mb-4 ${SECTION_HEADING_CLASS}`}>Timeline</h3>
      <ol className="space-y-0">
        {visibleEntries.map((entry, index) => (
          <li key={`${entry.Status}-${entry.CreatedDateStamp}-${index}`} className="flex gap-3">
            <div className="flex w-3 flex-col items-center">
              <Circle className="mt-1 size-2 fill-current text-muted-foreground" aria-hidden />
              {index < visibleEntries.length - 1 ? (
                <span className="w-px flex-1 bg-border" aria-hidden />
              ) : null}
            </div>
            <div className="pb-4">
              <p className="text-sm font-medium">{describePriorAuthHistoryStatus(entry.Status)}</p>
              {entry.AdditionalInfo ? (
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {entry.AdditionalInfo}
                </p>
              ) : null}
              {entry.CreatedDateStamp ? (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {formatDate(entry.CreatedDateStamp, true)}
                </p>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
